/**
 * RISKS-HUB
 * Central hub for navigating DUERP module
 */

import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import { Session } from "@/redux/_session.types"
import Card from "@/components/card"
import Button from "@/components/button"
import PageHeader from "@/components/page-header"
import {
  faSitemap,
  faExclamationTriangle,
  faFileAlt,
  faClipboardList,
  faDownload
} from "@fortawesome/free-solid-svg-icons"
import { ACCOUNT_DEMO } from "@/redux/account.types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import getColor from "@/utils/get-color.utils"
import { useEffect } from "react"
import { RisksHomeChart } from "./RisksHomeChart"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import jobStore from "../../job/model/job.store"
import unitStore from "../../unit/model/unit.store"
import Space from "@/components/space"
import { t } from "i18next"
import Tooltip from "@/components/tooltip"

interface ActionProps {
  icon: any
  label: string
  description: string
  buttonLabel: string
  buttonVariant?: "primary" | "secondary"
  onClick: () => void
  isLast?: boolean
  incompleteCount?: number
  onIncompleteClick?: () => void
  disabled?: boolean
  tooltip?: string
}

function RisksHubAction({
  icon,
  label,
  description,
  buttonLabel,
  buttonVariant = "secondary",
  onClick,
  incompleteCount,
  onIncompleteClick,
  disabled,
  tooltip
}: ActionProps) {
  const _session: Session = useAppSelector((state) => state._session)

  return (
    <Card
      className="flex flex-col p-6 hover:shadow-lg transition-shadow cursor-pointer"
      onClick={disabled ? undefined : onClick}>
      <div className="flex-1">
        <div className="flex">
          <FontAwesomeIcon
            icon={icon}
            size="2x"
            style={{ color: _session.accountColors.brandPrimary, marginBottom: 16 }}
          />
          {incompleteCount !== undefined && incompleteCount > 0 && (
            <>
              <Space />
              <Tooltip text={t("Cliquer pour voir les fiches à compléter")} position="top">
                <div
                  className="flex gap-4 items-center cursor-pointer transition-opacity hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation()
                    onIncompleteClick?.()
                  }}>
                  <div className="relative flex items-center justify-center">
                    <div
                      className="w-6 h-6 flex items-center justify-center rounded-[50%]"
                      style={{ backgroundColor: getColor("red") }}>
                      <FontAwesomeIcon icon={faClipboardList} className="text-sm text-white" />
                    </div>
                    <div
                      className="absolute -top-2 -right-3 w-5 h-5 rounded-full text-white flex items-center justify-center text-sm font-bold"
                      style={{ backgroundColor: getColor("red") }}>
                      {incompleteCount}
                    </div>
                  </div>
                  <div
                    className="text-xs px-2 py-1 rounded text-white w-fit h-fit"
                    style={{ backgroundColor: getColor("red") }}>
                    {t("À compléter")}
                  </div>
                </div>
              </Tooltip>
            </>
          )}
        </div>
        <h3 className="font-semibold text-base mb-2">{label}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <div className="flex flex-col justify-end mt-9">
        <Button
          className={disabled ? "" : buttonVariant}
          onClick={disabled ? undefined : onClick}
          isWithBorder={!disabled && buttonVariant === "secondary"}
          isFullWidth
          disabled={disabled}
          tooltip={tooltip}>
          {buttonLabel}
        </Button>
      </div>
    </Card>
  )
}

export function RisksHub() {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // selectors
  const _session: Session = useAppSelector((state) => state._session)
  const riskFamilies = useAppSelector(riskStore.selectors.listSelectedByUnits)
  const optionalRiskAttributes: string[] =
    useAppSelector(organizationStore.selectors.active)?.optionalRiskAttributes ?? []

  useEffect(() => {
    dispatch(unitStore.actions.clearSelected())
    dispatch(riskStore.actions.setFilter(null))
    dispatch(unitStore.thunks.fetchAndSet())
    dispatch(riskStore.thunks.fetchValidatedAndSet(null))
    dispatch(jobStore.thunks.fetchAndSet())
  }, [dispatch])

  const risksData = {
    validated: riskFamilies.flatMap((family: any) => family.Risks)
  }

  // Compter les fiches incomplètes (les attributs optionnels ne bloquent pas)
  function isRiskIncomplete(risk: any): boolean {
    return Object.entries(risk.attributes ?? {}).some(([key, value]: any) => {
      if (optionalRiskAttributes.includes(key)) return false
      return !value || value.trim?.() === ""
    })
  }

  const incompleteCount = risksData.validated.filter(isRiskIncomplete).length

  // Activer le filtre incomplete et naviguer vers les risques
  function goToIncompleteRisks() {
    navigate("/risks", { state: { filter: { incomplete: true } } })
  }

  const showImportButton = _session.accountId === ACCOUNT_DEMO.id && risksData.validated.length === 0

  return (
    <div>
      <PageHeader title={t("Bienvenue sur DUERP")} subtitle={t("Gérez vos risques et vos unités de travail")}>
        {showImportButton && (
          <Button className="primary" onClick={() => navigate("/risks/import")} icon={faDownload} isFullWidth>
            {t("Importer mes données")}
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-3 gap-6 mt-8 px-4">
        <RisksHubAction
          icon={faExclamationTriangle}
          label={t("Mes fiches de risques")}
          description={t("Identifier et évaluer les risques de votre organisation")}
          buttonLabel={t("Mettre à jour les risques")}
          buttonVariant="primary"
          onClick={() => navigate("/risks")}
          incompleteCount={incompleteCount}
          onIncompleteClick={goToIncompleteRisks}
          disabled={showImportButton}
          tooltip={showImportButton ? t("Importez vos données pour accéder à cette fonctionnalité") : undefined}
        />
        <RisksHubAction
          icon={faFileAlt}
          label={t("Document unique")}
          description={t("Consulter et exporter votre document unique")}
          buttonLabel={t("Voir le document unique")}
          buttonVariant="secondary"
          onClick={() => navigate("/risks/document")}
          disabled={showImportButton}
          tooltip={showImportButton ? t("Importez vos données pour accéder à cette fonctionnalité") : undefined}
        />
        <RisksHubAction
          icon={faSitemap}
          label={t("Mes unités de travail")}
          description={t("Gérer, consulter et organiser vos unités de travail")}
          buttonLabel={t("Gérer les unités de travail")}
          buttonVariant="secondary"
          onClick={() => navigate("/risks/units")}
          disabled={showImportButton}
          tooltip={showImportButton ? t("Importez vos données pour accéder à cette fonctionnalité") : undefined}
        />
      </div>

      <div className="grid grid-cols-2 gap-6 mb-8 px-4">
        <RisksHomeChart risks={risksData.validated} />
        <RisksHomeChart risks={risksData.validated} rpsOnly />
      </div>
    </div>
  )
}
